/**
 * WhatsApp Admin Dashboard — Majestic 2027
 * All API logic preserved. Visual redesigned to Celestial Intelligence.
 */

import React, { useState, useEffect } from 'react';
import AITextHelper from '../common/AITextHelper';
import AIBadge from '../common/AIBadge';
import TrainingGuide from '../common/TrainingGuide';
import whatsappSteps from '../../data/training/whatsapp';
import TableMobile, { TableMobileColumn, StatusTone } from '../responsive-fixes/TableMobile';
import { useResponsiveLayout } from '../responsive-fixes/useResponsiveLayout';

interface Conversation {
  phone: string;
  current_menu: string | null;
  message_count: number;
  last_interaction: string;
  last_message: string | null;
  lead_created: boolean;
  lead_id: string | null;
  in_live_agent_flow: boolean;
  awaiting_order: boolean;
}

interface Stats {
  total_conversations: number;
  leads_created: number;
  pending_leads: number;
}

interface OmniGroup {
  id: string;
  group_id: string;
  group_name: string;
  registered_by: string;
  is_active: boolean;
}

const WhatsAppDashboard: React.FC = () => {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [stats, setStats]             = useState<Stats>({ total_conversations: 0, leads_created: 0, pending_leads: 0 });
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [composeText, setComposeText] = useState("");
  const [groups, setGroups]           = useState<OmniGroup[]>([]);
  const [newGroupId, setNewGroupId]   = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [registeringGroup, setRegisteringGroup] = useState(false);
  const [activeTab, setActiveTab]     = useState<"conversations"|"groups"|"leads"|"compose">("conversations");

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch('/api/omnivoice/groups', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setGroups(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const handleRegisterGroup = async () => {
    if (!newGroupId.trim() || !newGroupName.trim()) return;
    setRegisteringGroup(true);
    try {
      const res = await fetch('/api/omnivoice/groups/register', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: newGroupId.trim(), group_name: newGroupName.trim(), registered_by: 'admin' }),
      });
      if (res.ok) {
        setNewGroupId(''); setNewGroupName('');
        const d = await fetch('/api/omnivoice/groups', { credentials: 'include' }).then(r => r.json());
        setGroups(Array.isArray(d) ? d : []);
      }
    } catch {}
    finally { setRegisteringGroup(false); }
  };

  const handleRemoveGroup = async (id: string) => {
    await fetch(`/api/omnivoice/groups/${id}`, { method: 'DELETE', credentials: 'include' });
    setGroups(g => g.filter(x => x.id !== id));
  };

  const fetchData = async () => {
    try {
      const [statsRes, convsRes] = await Promise.all([
        fetch('/api/whatsapp/stats'),
        fetch('/api/whatsapp/conversations')
      ]);
      if (!statsRes.ok || !convsRes.ok) throw new Error('Failed to fetch WhatsApp data');
      const statsData = await statsRes.json();
      const convsData = await convsRes.json();
      setStats(statsData);
      setConversations(convsData.conversations || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('971')) return `+971 ${cleaned.slice(3,5)} ${cleaned.slice(5,8)} ${cleaned.slice(8)}`;
    return phone;
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1)  return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getMenuLabel = (menu: string | null) => {
    if (!menu) return 'New';
    const labels: Record<string,string> = { main:'Main Menu', shop:'Shopping', best_sellers:'Best Sellers', orders:'Orders', shipping:'Shipping', brands:'Brands', location:'Location', live_agent:'Live Agent' };
    return labels[menu] || menu;
  };

  const TABS = [
    { id: "conversations" as const, icon: "chat",           label: "Active Dialogues" },
    { id: "groups"        as const, icon: "hub",            label: "Group Routing" },
    { id: "leads"         as const, icon: "person_search",  label: "WA Leads" },
    { id: "compose"       as const, icon: "edit",           label: "Compose" },
  ];

  if (loading) {
    return (
      <div className="m27-page" style={{ padding: "32px 40px" }}>
        <div style={{ display: "grid", gap: 12, maxWidth: 600 }}>
          {[80,60,90,50,70].map((w,i) => (
            <div key={i} style={{ width: `${w}%`, height: 14, borderRadius: 6, background: "rgba(189,178,255,0.08)", animation: "m27-shimmer 1.5s ease infinite" }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m27-page" style={{ padding: "32px 40px" }}>
        <div className="ultra-glass" style={{ borderRadius: 20, padding: 24, border: "0.5px solid rgba(255,107,107,0.3)" }}>
          <p style={{ color: "#ff6b6b", marginBottom: 16, fontFamily: "'Space Grotesk',sans-serif" }}>Error: {error}</p>
          <button onClick={fetchData} className="m27-btn m27-btn--ghost" style={{ fontSize: 12 }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="m27-page" style={{ padding: "32px 40px", position: "relative" }}>
      <div className="nebula-blob nebula-blob--green" style={{ top: -80, right: 120 }} />

      {/* Hero header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 40 }}>
        <div>
          <div className="m27-section-label" style={{ marginBottom: 10 }}>Core Consciousness: Active</div>
          <h1 className="m27-page-title">
            Active <span className="m27-page-title--accent">Dialogues</span>
          </h1>
          <p style={{ color: "rgba(221,229,255,0.4)", fontSize: 14, marginTop: 10, fontFamily: "'Space Grotesk',sans-serif" }}>
            Sifting through {stats.total_conversations.toLocaleString()} transmissions for peak clarity.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="m27-badge m27-badge--live" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="m27-dot m27-dot--secondary" style={{ animation: "m27-sonic-pulse 1.5s ease-out infinite" }} />
            Live
          </div>
          <button onClick={() => setTrainingOpen(true)} className="m27-btn m27-btn--ghost" style={{ padding: "8px 16px", fontSize: 11 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>help</span>
            Guide
          </button>
        </div>
      </div>

      <TrainingGuide moduleKey="whatsapp" steps={whatsappSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 32 }}>
        {[
          { label: "Active Conversations", value: stats.total_conversations, icon: "chat",          color: "#bdb2ff" },
          { label: "Leads Created",        value: stats.leads_created,       icon: "person_search", color: "#cfeda2" },
          { label: "Pending Leads",        value: stats.pending_leads,       icon: "pending",       color: "#ffd6a5" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} className="glass-panel" style={{ borderRadius: 18, padding: "20px 24px", position: "relative", overflow: "hidden" }}>
            <div className="beam-orbit" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div className="m27-section-label">{label}</div>
              <span className="material-symbols-outlined" style={{ color, fontSize: 18 }}>{icon}</span>
            </div>
            <div style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 900, fontSize: 36, color, letterSpacing: "-0.04em" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px",
            borderRadius: 8,
            border: activeTab === t.id ? "1px solid rgba(189,178,255,0.3)" : "1px solid rgba(255,255,255,0.06)",
            background: activeTab === t.id ? "rgba(189,178,255,0.12)" : "transparent",
            color: activeTab === t.id ? "#bdb2ff" : "rgba(221,229,255,0.4)",
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 12,
            cursor: "pointer", transition: "all 0.15s",
            boxShadow: activeTab === t.id ? "0 0 15px rgba(189,178,255,0.15)" : "none",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Conversations */}
      {activeTab === "conversations" && (
        <div className="ultra-glass" style={{ borderRadius: 20, overflow: "hidden" }}>
          {conversations.length === 0 ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: "rgba(189,178,255,0.3)", display: "block", marginBottom: 12 }}>chat</span>
              <div style={{ color: "rgba(221,229,255,0.5)", fontFamily: "'Space Grotesk',sans-serif" }}>No active conversations yet</div>
              <div style={{ color: "rgba(221,229,255,0.25)", fontSize: 12, marginTop: 6 }}>Conversations appear when customers message your WhatsApp</div>
            </div>
          ) : useMobileTable ? (
            // M3 fix (audit): below md the conversations table is a narrow strip.
            <div style={{ padding: 12 }}>
              <TableMobile<any>
                columns={[
                  { key: 'phone', label: 'Phone', primary: true,
                    format: (v: unknown) => formatPhone(v as string),
                  },
                  { key: 'in_live_agent_flow', label: 'Status', status: true,
                    format: (_v: unknown, row: Conversation) =>
                      row.in_live_agent_flow ? 'Live Agent' :
                      row.awaiting_order ? 'Order Track' : 'Active',
                  },
                  { key: 'current_menu', label: 'Menu',
                    format: (v: unknown) => getMenuLabel(v as string | null),
                  },
                  { key: 'last_message', label: 'Last Message',
                    format: (v: unknown) => (v as string) || '—',
                  },
                  { key: 'message_count', label: 'Msgs' },
                  { key: 'last_interaction', label: 'Last Active',
                    format: (v: unknown) => formatTime(v as string),
                  },
                  { key: 'lead_created', label: 'Lead',
                    format: (v: unknown) => (v as boolean) ? '✓ Created' : '—',
                  },
                ] as TableMobileColumn<any>[]}
                rows={conversations}
                statusTone={(row: Conversation): StatusTone =>
                  row.in_live_agent_flow ? 'info' :
                  row.awaiting_order ? 'warning' : 'success'
                }
              />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="m27-table">
                <thead>
                  <tr>
                    {["Phone","Status","Menu","Last Message","Messages","Last Active","Lead"].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {conversations.map(conv => (
                    <tr key={conv.phone}>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{formatPhone(conv.phone)}</td>
                      <td>
                        {conv.in_live_agent_flow
                          ? <span className="m27-badge m27-badge--tertiary">Live Agent</span>
                          : conv.awaiting_order
                          ? <span className="m27-badge m27-badge--primary">Order Track</span>
                          : <span className="m27-badge m27-badge--secondary">Active</span>
                        }
                      </td>
                      <td><span className="m27-badge m27-badge--primary">{getMenuLabel(conv.current_menu)}</span></td>
                      <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "rgba(221,229,255,0.4)", fontSize: 12 }}>{conv.last_message || "—"}</td>
                      <td style={{ textAlign: "center" }}><span className="m27-badge m27-badge--secondary">{conv.message_count}</span></td>
                      <td style={{ color: "rgba(221,229,255,0.4)", fontSize: 12 }}>{formatTime(conv.last_interaction)}</td>
                      <td>
                        {conv.lead_created
                          ? <span className="m27-badge m27-badge--secondary">✓ Created</span>
                          : <span style={{ color: "rgba(221,229,255,0.2)" }}>—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Group Routing */}
      {activeTab === "groups" && (
        <div className="ultra-glass" style={{ borderRadius: 20, padding: 28 }}>
          <p style={{ fontSize: 12, color: "rgba(221,229,255,0.4)", marginBottom: 20, fontFamily: "'Space Grotesk',sans-serif" }}>
            Registered groups receive @mention routing. Senders are auto-bridged to CRM leads.
          </p>
          {/* Register form */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24, padding: 16, background: "rgba(189,178,255,0.04)", borderRadius: 12, border: "0.5px solid rgba(189,178,255,0.1)" }}>
            <input className="m27-input" style={{ flex: "1 1 160px" }} placeholder="Group JID (e.g. 120363XXXXXX)" value={newGroupId} onChange={e => setNewGroupId(e.target.value)} />
            <input className="m27-input" style={{ flex: "1 1 140px" }} placeholder="Group name" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
            <button onClick={handleRegisterGroup} disabled={registeringGroup || !newGroupId.trim() || !newGroupName.trim()} className="m27-btn m27-btn--primary" style={{ padding: "10px 20px", fontSize: 12, opacity: registeringGroup ? 0.6 : 1 }}>
              {registeringGroup ? "Adding…" : "+ Register Group"}
            </button>
          </div>
          {groups.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px", color: "rgba(221,229,255,0.3)", fontFamily: "'Space Grotesk',sans-serif" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, display: "block", marginBottom: 10 }}>hub</span>
              No groups registered
            </div>
          ) : (
            <table className="m27-table">
              <thead><tr>{["Group Name","Group ID","Registered By","Status",""].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {groups.map(g => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 600 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 6, color: "#bdb2ff" }}>hub</span>
                      {g.group_name}
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: 11, color: "rgba(221,229,255,0.4)" }}>{g.group_id}</td>
                    <td style={{ fontSize: 12, color: "rgba(221,229,255,0.5)" }}>+{g.registered_by}</td>
                    <td>
                      <span className={`m27-badge ${g.is_active ? "m27-badge--secondary" : "m27-badge--error"}`}>
                        {g.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => handleRemoveGroup(g.id)} className="m27-btn m27-btn--ghost" style={{ padding: "4px 12px", fontSize: 11, color: "#ff6b6b", borderColor: "rgba(255,107,107,0.25)" }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab: WhatsApp Leads */}
      {activeTab === "leads" && (
        <div style={{ display: "grid", gap: 14 }}>
          {conversations.filter(c => c.lead_created).length === 0 ? (
            <div className="ultra-glass" style={{ borderRadius: 20, padding: "48px", textAlign: "center" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: "rgba(189,178,255,0.3)", display: "block", marginBottom: 12 }}>person_search</span>
              <div style={{ color: "rgba(221,229,255,0.5)", fontFamily: "'Space Grotesk',sans-serif" }}>No leads created yet</div>
              <div style={{ color: "rgba(221,229,255,0.25)", fontSize: 12, marginTop: 6 }}>Leads appear when customers request live agent support</div>
            </div>
          ) : conversations.filter(c => c.lead_created).map(conv => (
            <div key={conv.phone} className="glass-panel" style={{ borderRadius: 18, padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 14, color: "#dde5ff", marginBottom: 4 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 6, color: "#cfeda2" }}>phone</span>
                  {formatPhone(conv.phone)}
                </div>
                <div style={{ fontSize: 11, color: "rgba(221,229,255,0.3)", fontFamily: "monospace" }}>Lead ID: {conv.lead_id}</div>
                <div style={{ fontSize: 11, color: "rgba(221,229,255,0.3)", marginTop: 2 }}>{formatTime(conv.last_interaction)}</div>
              </div>
              <a href={`https://wa.me/${conv.phone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="m27-btn m27-btn--secondary" style={{ fontSize: 11, textDecoration: "none", padding: "8px 16px" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>open_in_new</span>
                Open Chat
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Tab: Compose */}
      {activeTab === "compose" && (
        <div className="ultra-glass" style={{ borderRadius: 20, padding: 28 }}>
          <div style={{ marginBottom: 12, fontSize: 12, color: "rgba(221,229,255,0.4)", fontFamily: "'Space Grotesk',sans-serif" }}>
            Compose a message <AIBadge /> — click ✨ to improve with AI
          </div>
          <AITextHelper
            value={composeText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComposeText(e.target.value)}
            placeholder="Type a message to a customer or group…"
            context="WhatsApp sales message"
            tone="friendly"
            rows={5}
          />
          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            <button
              className="m27-btn m27-btn--primary"
              style={{ opacity: composeText.trim() ? 1 : 0.4, fontSize: 12 }}
              onClick={() => { if (composeText.trim()) { console.info('[WhatsApp] Send:', composeText); setComposeText(''); } }}
              disabled={!composeText.trim()}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>send</span>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppDashboard;
